/**
 * The only place this console talks to the service.
 *
 * It holds no rules. Every number the console shows is computed on the service
 * side, because a console that derives its own view is a second implementation
 * of the routing policy that nobody reviews (contracts/operator-api.yaml).
 */

export interface Problem {
  type: string
  title: string
  status: number
  detail?: string | null
  field?: string | null
  correlation_id?: string | null
}

export class ApiError extends Error {
  readonly problem: Problem

  constructor(problem: Problem) {
    super(problem.title)
    this.problem = problem
  }

  get isForbidden(): boolean {
    return this.problem.status === 403
  }

  get isUnauthenticated(): boolean {
    return this.problem.status === 401
  }
}

export interface Page<T> {
  items: T[]
  next_cursor: string | null
  limit: number
}

/** Set by the authentication boundary when the development bypass is in use. */
let devUser: string | null = null

export function useDevUser(email: string | null): void {
  devUser = email
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase()
  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json, application/problem+json')

  if (method !== 'GET') {
    // The checked half of the CSRF defence, together with same site lax and a
    // JSON only API (ADR-006, deviation 3).
    headers.set('X-Requested-With', 'wall-e-console')
    headers.set('Content-Type', 'application/json')
  }
  if (devUser) headers.set('X-User', devUser)

  const response = await fetch(`/api/v1${path}`, {
    ...init,
    headers,
    credentials: 'include',
  })

  if (response.status === 204) return undefined as T

  const body = await response.json().catch(() => null)

  if (!response.ok) {
    throw new ApiError(
      body && typeof body === 'object' && 'title' in body
        ? (body as Problem)
        : {
            type: 'request-failed',
            title: 'The request could not be completed',
            status: response.status,
          },
    )
  }
  return body as T
}

/**
 * Reads a server-sent event stream with fetch rather than EventSource.
 *
 * EventSource cannot set request headers, which would force the development
 * identity into a query parameter, where it would land in every access log and
 * every browser history. Fetch carries the same headers as every other call
 * here, so the stream is authenticated exactly like the rest of the API.
 *
 * Returns a function that stops the stream.
 */
export function stream<T>(
  path: string,
  onEvent: (item: T) => void,
  onError?: (message: string) => void,
): () => void {
  const controller = new AbortController()

  void (async () => {
    try {
      const headers = new Headers({ Accept: 'text/event-stream' })
      if (devUser) headers.set('X-User', devUser)

      const response = await fetch(`/api/v1${path}`, {
        headers,
        credentials: 'include',
        signal: controller.signal,
      })
      if (!response.ok || !response.body) {
        onError?.(`The stream could not be opened (${response.status})`)
        return
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      for (;;) {
        const { done, value } = await reader.read()
        if (done) return
        buffer += decoder.decode(value, { stream: true })

        // Frames are separated by a blank line. A partial frame stays in the
        // buffer until the rest of it arrives.
        let split: number
        while ((split = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, split)
          buffer = buffer.slice(split + 2)
          for (const line of frame.split('\n')) {
            // Lines beginning with a colon are comments, which is what the
            // keep-alive is.
            if (!line.startsWith('data:')) continue
            try {
              onEvent(JSON.parse(line.slice(5).trim()) as T)
            } catch {
              // A malformed frame is not worth tearing the stream down for.
            }
          }
        }
      }
    } catch (err) {
      if ((err as Error)?.name !== 'AbortError') {
        onError?.('The stream stopped. Search to catch up.')
      }
    }
  })()

  return () => controller.abort()
}

export const api = {
  get: <T,>(path: string) => request<T>(path),
  post: <T,>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T,>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T,>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (path: string) => request<void>(path, { method: 'DELETE' }),
}

// ─── what the endpoints return ───────────────────────────────────

export interface Principal {
  email: string
  entra_object_id: string | null
  role: 'viewer' | 'operator' | 'admin'
  is_active: boolean
  via_dev_bypass: boolean
}

export interface AuthorityHealth {
  authority: string
  /** `unknown` is a real state and the honest one: we asked and could not tell,
   *  or there is no address to ask. It is never reported as `ok`. */
  status: 'ok' | 'degraded' | 'down' | 'unknown'
  timeout_ms: number
  upstream_reachable?: boolean | null
  /** Theirs: when that authority last checked its own upstream. Not the same
   *  fact as when we looked at the authority, and it used to be rendered as
   *  though it were. */
  upstream_checked_at?: string | null
  /** Ours: epoch seconds, when this service observed the lane. Present on
   *  every outcome, including the ones where nothing answered. */
  observed_at?: number | null
  /** Epoch seconds, the newest real call over this lane, or null when there
   *  was none in the window. Not the same fact as `observed_at`, which is the
   *  probe, and the probe presents no credential. */
  last_call_at?: number | null
  /** Why, in the words of whatever answered. "down" sends an operator to the
   *  authority; "the name did not resolve" sends them to the network. */
  detail?: string | null
  /** Whether this service holds a credential for this lane. Separate from
   *  status because they answer different questions: Service OCPI's health
   *  endpoint needs no credential, so it answers ok while every forward is
   *  refused for want of one. */
  credential?: 'set' | 'missing' | 'not-required'
}

export interface OverviewSeries {
  from: string
  to: string
  bucket_seconds: number
  /** How wide one point is, in words, so the page states its own resolution
   *  rather than leaving it to be guessed from the spacing of the axis. */
  bucket_label: string
  /** The zone a day boundary is taken in. A day is only a day in some zone. */
  zone: string
  /** The widest range the service answers for, sent so the console need not
   *  keep a second copy of the number. */
  max_range_days: number
  buckets: string[]
  decisions: {
    by_outcome: Record<string, number[]>
    /** Null where nothing was decided. An hour with no authorizations is not
     *  an hour in which nothing was refused. */
    refusal_rate: Array<number | null>
    total: number
  }
  latency: {
    p50: Array<number | null>
    p95: Array<number | null>
    p99: Array<number | null>
    /** Over the whole range, for the card above the charts. Null when nothing
     *  was measured. A p95 of hourly p95s is not a p95, so the service sends it. */
    overall?: { p50: number | null; p95: number | null; p99: number | null }
    window_ms: number
  }
  lanes: Record<string, number[]>
  settlements: {
    by_outcome: Record<string, number[]>
    total: number
    /** Where the record begins. `recorded_at` arrived with migration 0010, so
     *  outcomes written before it carry no time and appear on no chart. Null
     *  when nothing has been recorded yet. */
    recorded_from: string | null
  }
  orphans: {
    by_operation: Record<string, number[]>
    /** Of those, the ones carrying nothing that could be looked up. Kept apart
     *  because a chart showing an orphan with no decision behind it reads as
     *  "we looked and found nothing", and most of these were never lookable. */
    not_searchable: number[]
    total: number
    total_not_searchable: number
  }
}

export interface UnownedCounts {
  enabled: boolean
  last_hour: Record<string, number>
  last_day: Record<string, number>
  /** Unstamped events forwarded as before and counted (DEC-25),
   *  keyed no_start_seen or no_transaction_id. */
  forwarded_last_hour?: Record<string, number>
  forwarded_last_day?: Record<string, number>
  /** Kept events whose body carried no event time. */
  no_event_time_last_day?: number
  /** Starts whose owner was looked for around their arrival, not their own time. */
  anchored_on_arrival_last_day?: number
}

export interface Overview {
  decisions_per_hour: number
  /** Decisions in the same window whose token could not be encrypted, so
   *  their row shows none. The authorizations went on. Zero is the only good
   *  value. Optional only for a console running ahead of its service. */
  tokens_not_kept?: number
  /** Null when no authorization was presented in the window. Not zero: a rate
   *  of zero says nothing is being refused, and an empty hour is a different
   *  fact the card has to be able to say. */
  refusal_rate: number | null
  /** What "per hour" is measured over, so the caption is not the only place
   *  that knows. */
  window_seconds?: number
  /** Settlements this service has stopped trying to deliver. Each is a session
   *  an authority refused in a way resending cannot fix, so it is money nobody
   *  will be billed for until a person completes it. */
  /** Triggers whose silence is a safety matter and which currently reach
   *  nobody, named by the service so this page and Settings cannot disagree
   *  about what "reaches somebody" means. Absent from an older service. */
  alerts_reaching_nobody?: string[]
  settlements_abandoned?: number
  /** Hubject bridge forwards this service has stopped trying to deliver. A
   *  different question from the number above: each may be a session Hubject
   *  is holding open, or one they were never told ended, and the person who
   *  fixes it is not the person who completes a settlement. */
  bridge_forwards_abandoned?: number
  /** Settlements that were never formed, by reason, over the last day. A third
   *  state beside abandoned and failed: never queued, nothing will retry it,
   *  and the stop does not come again. By reason because the reasons are
   *  acted on differently (FIND-49). */
  settlements_incomplete?: Record<string, number>
  /** Starts and stops nobody owns, kept here and sent to no service, by
   *  reason over the hour and the day (DEC-25). */
  unowned_events?: UnownedCounts
  /** Orphans inside the window, beside the total. The total only grows and
   *  nothing deletes it, so on its own it can never show that a repair
   *  upstream worked. */
  orphan_session_events_recent?: number
  orphan_window_seconds?: number
  latency_p95_ms: number
  authorities: AuthorityHealth[]
  /** How estate membership is decided. It was `location_tree` with three
   *  literals behind it and a green badge on top; there is no tree, and the
   *  rule is a prefix test on the charge point id (FIND-45). */
  estate: {
    decided_by: string
    prefix: string
    name: string
    tree: null
    tree_note?: string
    /** The local copy of the E4E stations, and what it would have decided
     *  (FIND-45, DEC-22 step 2). It routes nothing yet. */
    catalogue?: {
      configured: boolean
      stations: number
      names: number
      connectors: number
      /** Which mark admitted these stations, measured from the list rather
       *  than declared. Today that is ['e4e.cpid'] everywhere; the Hub is
       *  adding an explicit estate mark and this changes with the data. */
      marks?: string[]
      /** What the list cannot answer. A current type is a fuel grade, so a
       *  panel showing the size of the list without these invites a reader to
       *  assume the rest is known. */
      connectors_unknown?: number
      stations_without_connectors?: number
      stations_mixed?: number
      offered?: number | null
      pulled_at?: string | null
      attempted_at?: string | null
      last_error?: string | null
      shadow: {
        compared: number
        /** The money direction: these charge points would START going to TIM.
         *  A list by station, never a count, because a count says "three" and
         *  tells nobody which three. */
        would_route_to_tim: Array<{
          station_id: string
          decisions: number
          bmw_cards: number
          first_seen: string | null
          last_seen: string | null
          matched_name: string | null
          matched_namespace: string | null
          /** How many of this station's decisions matched through a name it
           *  has stopped using. A count, not a flag: a station can be reached
           *  under a current name and a retired one in the same week. */
          through_retired_name?: number
        }>
        /** These would STOP going to TIM, which somebody reports the same day. */
        would_leave_tim: number
        ambiguous_names: number
      }
    }
  }
  stamps: { pending: number; bound: number; expired: number }
  unstamped_starts: number
  orphan_session_events: number
  /** Verdicts this process could not hand to the Integration Hub. */
  undelivered_verdicts: number
}

export interface DecisionSummary {
  correlation_id: string
  occurred_at: string | number | null
  station_id: string
  identifier_blind_index: string
  /** The token exactly as the station sent it, for the signed-in reader
   *  (2026-10-17). Null unless `identifier_record` is `kept`. */
  identifier?: string | null
  /** Why `identifier` is or is not there. */
  identifier_record?:
    | 'kept'
    | 'decided_before_tokens_were_kept'
    | 'could_not_be_encrypted'
    | 'could_not_be_decrypted'
  scheme: string
  route: string
  /** A canary authorization (DEC-29), sent by the canary runner and never by
   *  a charge point. Absent from a service before DEC-29. */
  canary?: boolean
  authority: string | null
  outcome: 'APPROVED' | 'BLOCKED' | 'UNKNOWN'
  basis: string
  /** The class of card, in the Globale RFID Liste vocabulary the OCPP Server
   *  reads: TIM_BMW_FLEET, DCS_CHARGE_NOW and the rest. Null is a real answer,
   *  not a gap: Service OCPI sends the class only on APPROVED, so a refusal on
   *  that lane genuinely has none, and it is never filled with a guess. */
  token_class: string | null
  /** What the authority actually said. `basis` says which kind of answer this
   *  was, this says what it was. Both are needed: authority_unavailable covers
   *  a dead authority and a live one refusing our credential (FIND-02). */
  reason: string | null
  /** WHO DECIDED, WHICH THE LANE DOES NOT SAY. `authority` is 'dcs' for every
   *  answer on the default route, including the ones Service OCPI took by its
   *  own rule and the ones nothing answered at all. Service OCPI's vocabulary
   *  (wall-e, dcs, hubject, nobody), 'tim' where TIM answered, and 'eas' where
   *  no authority stated a verdict and this service chose the outcome.
   *
   *  Deliberately a string and not a union: the vocabulary is theirs and they
   *  may add to it without a release here. Null is "not recorded", which is
   *  every decision from before this existed (FIND-43). */
  decided_by: string | null
  /** Whether DCS was actually called. Null is not false: false is Service OCPI
   *  saying it did not call DCS, null is nobody having said. */
  dcs_called: boolean | null
  /** The token's issuer or party. Never the card number (DEC-03). */
  issuer: string | null
  /** What DCS answered, in Service OCPI's word: ALLOWED, NOT_ALLOWED,
   *  UNKNOWN_TOKEN, TIMEOUT and whatever else DCS puts in `allowed`. Null when
   *  DCS was not asked.
   *
   *  TIMEOUT is any failure of their DCS call that is not an unknown token,
   *  connection errors included, so it is worded as "DCS did not answer" and
   *  never as slowness: their own DCS timeout is 1.0 s, nowhere near this
   *  service's 3 s budget. */
  dcs_response: string | null
  /** What the E4E station list would have said about this charge point, which
   *  decided nothing: the prefix rule routed this decision (DEC-22 step 2). */
  catalogue_shadow: {
    prefix: boolean
    /** A UNION AND NOT `string`, so a sixth state is a compile error here
     *  rather than a sentence rendered by a fallthrough. It was `string`, and
     *  the reading below had a default that said "not in the E4E list": the
     *  fifth state, `in_list_not_member`, means the list HOLDS the station
     *  and says it is not ours, so the default would have rendered the exact
     *  opposite of the truth, confidently, on the page an operator reads. */
    catalogue: Membership
    /** Which rule admitted the station to the list, where the list says.
     *  Null is a row stored before the column existed. */
    membership_via?: string | null
    name: string | null
    namespace: string | null
    claimants?: number | null
    current_type: string
    /** True only when the name that matched is one the station no longer
     *  answers to. Absent otherwise, because false on every row would be a
     *  column of noise: the question is only asked of a match. */
    retired_name?: boolean | null
  } | null
  /** What kind of identifier arrived, in Service OCPI's words: card_uid,
   *  placeholder or other. They ask DCS only about something shaped like a
   *  card, so this is what separates "the card is in no list" from "the charge
   *  point sent no card number". Null on every row before they sent it, and
   *  the console then says only what is known. */
  id_token_kind: string | null
  latency_ms: number
}

export interface PredicateStep {
  step: string
  matched: boolean
  detail: string | null
}

export interface StampView {
  state: 'PENDING' | 'BOUND' | 'EXPIRED'
  bound_at: number | null
  session_id: string | null
  broker_transaction_id: string | null
}

export interface SettlementView {
  status: number | null
  outcome: string
  differing_field: string | null
  escalated: boolean
  acknowledged_by: string | null
  note: string | null
}

/** One card on the Attempts page: a pseudonym, its refusals and its flags. */
export interface AttemptCard {
  /** The blind index. Attempts names a card by its pseudonym, never its token. */
  card: string
  taps: number
  refused: number
  approved: number
  stations: number
  refused_stations: number
  first_seen: string | null
  last_seen: string | null
  scheme: string | null
  issuer: string | null
  decided_by: Array<{ word: string; seen: number }>
  reasons: Array<{ word: string; seen: number }>
  /** Set when the row is not a card at all: a charge point sending a literal
   *  or its own id where an identifier belongs (FIND-29). Such a row carries
   *  no flags, because it is not somebody trying anything. */
  placeholder: { kind: string; detail: string } | null
  /** What Service OCPI called this identifier, where it said: card_uid,
   *  placeholder, other. An array because a row is an aggregate and a silent
   *  pick would hide a card that somehow arrived under two. */
  kinds: string[]
  keeps_trying: boolean
  several_stations: boolean
}

export interface AttemptStation {
  station_id: string
  cards_refused: number
  refused: number
  taps: number
  common_reason: string | null
  first_seen: string | null
  last_seen: string | null
}

export interface AttemptsAnswer {
  window: { from: string; to: string; max_days: number }
  /** The thresholds, in words, so the page can print the rule beside the flag
   *  it draws. A flag whose rule is not on screen cannot be argued with. */
  rules: { keeps_trying: string; several_stations: string; collapse: string }
  cards: AttemptCard[]
  stations: AttemptStation[]
}

export interface DecisionDetail {
  summary: DecisionSummary
  predicate_steps: PredicateStep[]
  authority_call: { status: number | null; reason: string | null } | null
  stamp: StampView | null
  settlement: SettlementView | null
  /** Where this session's submission is in the TIM console, or null when no
   *  address is configured. Built by the service so the link is either right
   *  or absent: the console cannot assemble one from a base it half knows. */
  tim_submission_url: string | null
  /** Where this decision's audit row is in the Service OCPI dashboard, built
   *  by the service so it is either right or absent. Null when they sent no
   *  row id, and null while no address is configured in Settings. */
  ocpi_decision_url: string | null
  latency_breakdown: { own_ms: number; downstream_ms: number }
}

export interface MessageSummary {
  message_id: string
  occurred_at: number
  direction: 'inbound' | 'outbound'
  peer: string
  operation: string
  correlation_id: string | null
  latency_ms: number | null
  outcome: string | null
}

export interface StorageReport {
  tables: Array<{
    name: string
    estimated_rows: number
    bytes: number
    /** Null when the table has never been analysed, which is not the same as
     *  empty although both report zero rows. */
    counted_at: string | null
    /** How far back this table goes, and how recent it is. Null for a table
     *  with no rows or no time column. Two index-only scans, so it is on the
     *  page; rows and bytes by age is a sequential scan and is not. */
    oldest?: string | null
    newest?: string | null
  }>
  total_bytes: number
  /** The service says whether the counts are estimates, rather than the page
   *  carrying a caveat that would go on being shown after it stopped being
   *  true. */
  counts_are: 'estimated'
}

export interface LogLine {
  occurred_at: number
  level: string
  logger: string
  message: string
  correlation_id: string | null
}

export interface RoutingRuleView {
  scheme: string
  prefix: string
  authority: string
  requires_estate: string | null
  state: 'ACTIVE' | 'TRANSITIONAL' | 'SUNSET'
  sunset_at?: string | null
  enabled: boolean
}

export interface RoutingPolicyView {
  version: string
  hash: string
  author?: string | null
  note?: string | null
  created_at?: string | null
  /** Whether this caller may save. The endpoint checks the role again. */
  editable?: boolean
  rules: RoutingRuleView[]
  /** Not reasons to refuse. Today: a sunset date BMW owes and has not given. */
  warnings?: string[]
  excluded_stations: string[]
  diff?: PolicyDiff
}

export interface PolicyDiff {
  added: RoutingRuleView[]
  removed: RoutingRuleView[]
  changed: {
    prefix: string
    fields: { field: string; from: unknown; to: unknown }[]
  }[]
}

export interface PolicyVersion extends RoutingPolicyView {
  diff: PolicyDiff
}

export interface DryRun {
  route: string
  authority: string | null
  refused: boolean
  basis: string | null
  steps: { step: string; matched: boolean; detail: string | null }[]
}

/** A session event that named no stamp, and what the policy would have made
 *  of it. Every field after the first four can be legitimately absent, and the
 *  absences mean different things (see pages/orphanReading). */
export interface OrphanSessionEvent {
  id: number
  observed_at: number
  operation: string
  station_id: string
  broker_transaction_id?: string | null
  /** Never the identifier. Personal data does not leave the database. */
  identifier_blind_index?: string | null
  scheme?: string | null
  would_have_been?: string | null
  route_label?: string | null
  route_steps?: string[]
  in_estate?: boolean | null
  forward_status?: number | null
  forward_latency_ms?: number | null
  /** Whether a decision this service already recorded explains this row, and
   *  when it cannot, why not. Four answers rather than a blank: a blank says
   *  no authorization existed, which for almost every row on production is the
   *  opposite of the truth. */
  decision?: OrphanDecision
}

export interface OrphanDecision {
  state: 'found' | 'none' | 'several' | 'not-searchable'
  detail?: string | null
  candidates?: number
  /** How long before the start the nearest authorization was. Null when it
   *  cannot be read, which is not the same as zero. */
  seconds_before?: number | null
  /** Whether that gap is inside the window where it could still have bound.
   *  False is the third answer this page could not give before: there WAS an
   *  authorization, and it was too late. */
  within_bind_window?: boolean | null
  /** How far back the search looked, so a negative can be bounded. "None
   *  within two hours" is supportable; "no authorization" is not. */
  searched_seconds?: number | null
  bind_window_seconds?: number | null
  correlation_id?: string | null
  outcome?: string | null
  basis?: string | null
  authority?: string | null
}

export interface AuthorityEndpoint {
  authority: string
  url: string
  timeout_ms: number
  /** Where the credential for this authority actually lives. Not here. */
  secret_lives_in: string
  /** Where the address itself comes from. Not here either: it moves by
   *  deployment, and the page reports what the service would dial rather than
   *  a stored copy of it. */
  source: string
  /** Whether this service holds a credential for this lane. */
  credential?: 'set' | 'missing' | 'not-required'
  /** Whether this way out can be asked how it is. The Integration Hub cannot:
   *  it is fire and forget and publishes no health we agreed to read. */
  testable?: boolean
  /** How many recent real calls the status is built on. Zero means the health
   *  check answered and nothing has exercised the credential, and a probe
   *  carries none (FIND-02). Optional, so an older service reads as zero,
   *  which is the safe direction: absent must not look like confirmed. */
  recent_calls?: number
  /** Where the lane stands right now, so a card says something before anybody
   *  presses Test. */
  status?: 'ok' | 'degraded' | 'down' | 'unknown'
  status_detail?: string | null
  /** Epoch seconds, when this service last looked at this lane. */
  observed_at?: number | null
  /** Epoch seconds, the newest REAL call over this lane inside the window the
   *  verdict is built on, or null when there was none.
   *
   *  APART FROM `observed_at`, which is when the probe looked. The probe
   *  carries no credential on purpose, so only this one can answer "has
   *  anything presented the key we hold". Optional, so an older service reads
   *  as null, which is the safe direction here: unknown must not look like
   *  presented. */
  last_call_at?: number | null
}

/** What the station list says about one charge point.
 *
 *  THE SERVICE'S OWN FIVE, from `core/enums.Membership`. Kept as a union so
 *  the console cannot be given a state it has no sentence for: a sixth value
 *  fails `tsc` here, which is the only place that can catch it before an
 *  operator reads a wrong sentence.
 */
export type Membership =
  | 'member'
  | 'not_member'
  | 'several'
  | 'list_empty'
  | 'in_list_not_member'

export interface Settings {
  /** How long a saved switch or key can take to reach an authorization: at
   *  once on the replica that saved it, within this on the others (DEC-28 C4). */
  takes_effect_within_seconds?: number
  /** Read only. How a principal is established lives in the environment. */
  sign_in?: {
    tenant_id: string
    client_id: string
    development_bypass: boolean
    password_enabled: boolean
    source: string
  }
  inbound: {
    broker_api_key_set: boolean
    broker_api_key_updated_at: number | null
    /** The Service OCPI key's rotation slot (DEC-27). Whether it is set, never the value. */
    ocpi_api_key_next_set: boolean
    /** The OCPP Admin's key for the Wall-E list, and its rotation slot (DEC-28). */
    wall_e_list_api_key_set: boolean
    wall_e_list_api_key_updated_at: number | null
    wall_e_list_api_key_next_set: boolean
    wall_e_list_api_key_next_updated_at: number | null
    /** The canary runner's key, and its rotation slot (DEC-29). */
    canary_key_set?: boolean
    canary_key_updated_at?: number | null
    canary_key_next_set?: boolean
    canary_key_next_updated_at?: number | null
    external_api_path: string
    events: string[]
  }
  authorities: AuthorityEndpoint[]
  /** Where each lane lives, and which of the two copies is in force.
   *
   *  THE VALUE IS READ BACK IN FULL, unlike the key beside it. The failure an
   *  address field prevents is somebody unable to tell which of two values is
   *  live, so hiding it would be the defect rather than the caution.
   *
   *  Only addresses something resolves at use time are here. `ocpi_url` is set
   *  in every deployment and no caller reads it this way, so an entry would be
   *  a field that changes nothing. */
  addresses: {
    name: string
    label: string
    why: string
    value: string
    /** Typed into the console, the deployment, or nothing anywhere. Three,
     *  because prod is in the third for the station list right now. */
    in_use_from: string
    deployed: string
    updated_at: number | null
  }[]
  /** The switches that change where a card goes.
   *
   *  `on` is the state IN FORCE, which is the stored value when there is one
   *  and the deployment otherwise, so a page cannot show off for a lane that
   *  is on. `requires` is what must already be on before this one may be, and
   *  it is here so the page can explain a refusal before showing it. */
  switches: {
    name: string
    label: string
    why: string
    on: boolean
    requires: string[]
    required_by: string[]
    updated_at: number | null
  }[]
  /** What the console reads from, which is a different relationship from what
   *  the service writes to. The Hub is both, and only this direction carries a
   *  credential. */
  sources: {
    source: string
    scheme: 'bearer' | 'api-key'
    is_set: boolean
    updated_at: number | null
    what_for: string
    /** Which of the two places the value actually in use came from, never the
     *  value. A stored one wins over the deployment's, so "set" alone lets
     *  somebody rotate a Key Vault secret and believe it took effect while a
     *  stored value is still what gets presented. `unreadable` is a stored
     *  value that cannot be decrypted: present, meant to be used, and not
     *  usable, which is not the same as missing. */
    in_effect?: 'stored' | 'deployment' | 'unreadable' | 'none'
    /** The setting this source reads. Two sources sharing one setting is the
     *  Hub's two reads: one consumer, one key. */
    setting?: string
  }[]
  /** One row per lane that wants identifying. Derived from a single
   *  declaration in the service, so a lane that gains a credential cannot keep
   *  reporting that it needs none. */
  credentials: {
    authority: string
    scheme: 'bearer' | 'api-key'
    is_set: boolean
    updated_at: number | null
    /** Where the key in use comes from, never the key. Absent from a
     *  service older than this console. */
    in_effect?: 'stored' | 'deployment' | 'unreadable' | 'none'
  }[]
  mail: {
    host: string | null
    port: number | null
    username: string | null
    password_set: boolean
    password_updated_at: number | null
    default_sender: string | null
    last_test: { attempted_at: number; succeeded: boolean; detail: string | null } | null
  }
  retention_days: number
  /** Where the TIM console lives, for the link out of a decision. Empty means
   *  no link is shown at all, rather than one that goes nowhere. */
  tim_console_url?: string
  ocpi_dashboard_url?: string
  /** DEC-26: the providers whose Plug and Charge contracts are never taken to
   *  the connector, as its BMW Estate page shows them. */
  dcs_contract_providers?: string
  /** The alert triggers anything in the service actually sends. The rest are
   *  offered as not sent yet. Absent from a service older than this console. */
  alerts_raised?: string[]
  /** How many people each raised trigger actually reaches, computed by the
   *  service with the same function the sender uses. Not derived here: two
   *  implementations of "an enabled rule with at least one recipient" can
   *  disagree, and the direction that costs money is this page saying
   *  somebody is told while the sender posts to an empty list. */
  alerts_reach?: Record<string, number>
  /** The triggers whose silence is a safety matter, so the warning set is the
   *  service's and cannot drift from it. */
  alerts_must_reach?: string[]
  alerts: { trigger: string; recipients: string[]; enabled: boolean }[]
}

/** What `POST /settings/alerts/{trigger}/test` answers. A COUNT and never the
 *  addresses: the page already has them for editing, and a result is copied
 *  into tickets and screenshots where they have no business. */
export interface AlertTestResult {
  sent: boolean
  recipients: number
  detail?: string
}

// ─── the Wall-E list (DEC-28) ─────────────────────────────────────

export const WALL_E_LIST_KINDS = [
  'card', 'label', 'placeholder', 'kiosk', 'vehicle', 'test', 'other',
] as const
export type WallEListKind = (typeof WALL_E_LIST_KINDS)[number]

export interface WallEListEntry {
  id: string
  /** As stored. Null only where it could not be decrypted. */
  token: string | null
  kind: WallEListKind
  status: 'accepted' | 'blocked'
  note: string | null
  created_at: string
  created_by: string
  created_via: 'console' | 'ocpp_admin' | 'import'
  updated_at: string
  updated_by: string
  updated_via: 'console' | 'ocpp_admin' | 'import'
  last_used_at: string | null
  deleted: boolean
  revision: number
}

export interface WallEListPage {
  entries: WallEListEntry[]
  next_cursor: string | null
  revision: number
}

export type ImportVerdict = 'new' | 'unchanged' | 'conflicting' | 'repeated' | 'invalid'

export interface WallEListImport {
  dry_run: boolean
  counts: Record<ImportVerdict, number>
  written: number
  rows: {
    line: number
    token: string | null
    status: 'accepted' | 'blocked' | null
    kind: WallEListKind | null
    verdict: ImportVerdict
    existing_id: string | null
    why: string | null
  }[]
}

// ─── totals, the Overview and Day by day (DEC-28 C) ───────────────

export interface Latency {
  p50: number | null
  p95: number | null
  own_p50: number | null
  own_p95: number | null
  wait_p50: number | null
  wait_p95: number | null
}

/** The fields a "Why" is worded from, and how many decisions share them. The
 *  page words a group with the same function it words a row with. */
export interface ReasonGroup {
  outcome: 'APPROVED' | 'BLOCKED' | 'UNKNOWN'
  basis: string
  reason: string | null
  decided_by: string | null
  authority: string | null
  dcs_called: boolean | null
  dcs_response: string | null
  id_token_kind: string | null
  count: number
}

export interface DecisionTotals {
  from: string
  to: string
  total: number
  by_outcome: Record<'APPROVED' | 'BLOCKED' | 'UNKNOWN', number>
  by_decided_by: { decided_by: string | null; outcome: string; count: number }[]
  why_not: ReasonGroup[]
  distinct_tokens: number
  distinct_charge_points: number
  latency: Latency
  bucket: 'hour' | 'day'
  buckets: { t: string; approved: number; blocked: number; unknown: number }[]
  second_ask_share: number | null
}

export interface QueueState {
  waiting: number
  oldest_waiting_at: string | null
  failed: number
}

export interface Dashboard {
  range: 'today' | '7d' | '30d'
  from: string
  to: string
  totals: { asks: number; APPROVED: number; BLOCKED: number; UNKNOWN: number }
  lanes: {
    lane: 'dcs' | 'tim' | 'hubject' | 'wall_e_list'
    by_outcome: Record<'APPROVED' | 'BLOCKED' | 'UNKNOWN', number>
    latency: Latency
    last_failure_at: string | null
  }[]
  refusals: ReasonGroup[]
  plug_and_charge: {
    asks: number
    contracts: number
    latency: Latency
    by_authority: { authority: string | null; outcome: string; count: number; contracts: number }[]
  }
  sessions: {
    started: number
    stopped: number
    to_ocpi: number
    to_tim: number
    to_hubject: number
    kept: number
  }
  queues: { tim_settlements: QueueState; hubject_forwards: QueueState }
  switches: { name: string; label: string; on: boolean; updated_at: number | null }[]
}

export interface DailyFigures {
  asks: number
  approved: number
  unknown: number
  blocked: number
  starts: number
  stops: number
  charge_points: number
  tokens: number
  charges_per_charge_point: number | null
  asks_per_charge: number | null
}

export interface DailyRow extends DailyFigures {
  day: string
  weekday: 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun'
  partial: boolean
  baseline: (DailyFigures & { of_days: string[] }) | null
}

export interface Daily {
  zone: 'Europe/Berlin'
  as_of: string
  days: DailyRow[]
}

/** GET /system-health: the dependencies, each coloured by the service's rule. */
export type HealthStatus = 'ok' | 'warning' | 'error'

export interface SystemHealthRow {
  id: string
  name: string
  status: HealthStatus
  value: string
}

export interface BuildInfo {
  version: string | null
  commit: string | null
  environment: string | null
}

export interface SystemHealth {
  overall: 'nominal' | 'degraded' | 'down'
  checked_at: string
  rows: SystemHealthRow[]
  build: BuildInfo
}
